import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DownloadSimple, CaretDown } from "@phosphor-icons/react";
import { downloadDailyCsv } from "./csvExports";

/**
 * Tab body: "Per-campaign Reconciliation" — biggest of the tabs.
 *
 * Top: 4 totals cards (feed revenue / pub earnings / margin / cap-fire
 * days). The cap-fire card switches to amber when violations > 0 so
 * it stands out on dashboards.
 *
 * Below: per-day expandable rows. Click a day → reveals the
 * per-publisher table for that date. We collapse by date client-side
 * so we keep a single `/reports/daily` fetch + don't need a separate
 * endpoint per pivot.
 *
 * CSV export at top right is wired to the in-memory `dailyReport` so
 * what you see is exactly what you get.
 */
export default function ReconciliationTab({
  campaignReport, dailyReport, openReconcDate, onToggleDate,
  reconcStart = "", reconcEnd = "", onWindowChange,
  activeWindow = { start: "", end: "" }, loading = false,
}) {
  if (!campaignReport) return <div className="text-sm text-slate-400">Loading…</div>;

  // Collapse daily rows by date so each date is one expandable group.
  const byDate = new Map();
  (dailyReport?.data || []).forEach((r) => {
    const g = byDate.get(r.date) || {
      date: r.date,
      rows: [],
      our_clicks: 0, feed_clicks: 0,
      feed_revenue: 0, pub_earnings: 0, margin: 0,
      any_cap: false, any_pending: false,
    };
    g.rows.push(r);
    g.our_clicks += r.our_clicks;
    g.feed_clicks += r.feed_clicks;
    g.feed_revenue += r.feed_revenue;
    g.pub_earnings += r.pub_earnings;
    g.margin += r.margin;
    if (r.cap_fired) g.any_cap = true;
    if (r.pending) g.any_pending = true;
    byDate.set(r.date, g);
  });
  const days = [...byDate.values()].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <>
      {/* Date-window picker. On first paint the inputs are pre-filled
          from the server-side smart default (latest synced date − 30 days)
          so the user sees exactly which window they're viewing. */}
      <div className="mb-4 flex items-end gap-2 flex-wrap" data-testid="sn-reconc-window">
        <div>
          <label className="block text-[10px] font-bold tracking-[0.22em] uppercase text-slate-500 mb-1">From</label>
          <Input
            type="date"
            value={reconcStart || activeWindow.start || ""}
            onChange={(e) => onWindowChange?.(e.target.value, reconcEnd || activeWindow.end || "")}
            className="w-[160px]"
            data-testid="sn-reconc-start"
            disabled={loading}
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold tracking-[0.22em] uppercase text-slate-500 mb-1">To</label>
          <Input
            type="date"
            value={reconcEnd || activeWindow.end || ""}
            onChange={(e) => onWindowChange?.(reconcStart || activeWindow.start || "", e.target.value)}
            className="w-[160px]"
            data-testid="sn-reconc-end"
            disabled={loading}
          />
        </div>
        {(reconcStart || reconcEnd) && (
          <Button
            variant="ghost"
            size="sm"
            className="text-[11px] text-slate-500 hover:text-slate-900"
            onClick={() => onWindowChange?.("", "")}
            data-testid="sn-reconc-reset"
          >
            Reset to latest
          </Button>
        )}
        <div className="text-[11px] text-slate-400 ml-1">
          Window auto-anchors on the latest synced date so you always land on real data.
        </div>
      </div>

      <div className="mb-4 grid grid-cols-4 gap-3">
        <div className="border border-slate-200 rounded-md p-4 bg-white">
          <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-400">Feed revenue</div>
          <div className="text-xl font-mono font-bold text-slate-900 mt-1">
            ${Number(campaignReport.totals?.feed_revenue || 0).toFixed(2)}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">what Shopnomix paid us</div>
        </div>
        <div className="border border-slate-200 rounded-md p-4 bg-white">
          <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-400">Pub earnings</div>
          <div className="text-xl font-mono font-bold text-emerald-700 mt-1">
            ${Number(campaignReport.totals?.pub_earnings || 0).toFixed(2)}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">what we owe publishers</div>
        </div>
        <div className="border border-slate-200 rounded-md p-4 bg-white">
          <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-400">Margin</div>
          <div className="text-xl font-mono font-bold text-[#002FA7] mt-1">
            ${Number(campaignReport.totals?.margin || 0).toFixed(2)}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            {Number(campaignReport.totals?.take_pct || 0).toFixed(1)}% take
          </div>
        </div>
        <div
          className={`border rounded-md p-4 ${
            (campaignReport.totals?.cap_violations || 0) > 0
              ? "bg-amber-50 border-amber-200"
              : "bg-white border-slate-200"
          }`}
          title={`Days where feed EPC > $${Number(campaignReport.payout_cap_usd || 0.27).toFixed(2)} — we capped pub at the ceiling and kept the extra upside`}
        >
          <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-400">
            ≥${Number(campaignReport.payout_cap_usd || 0.27).toFixed(2)} days
          </div>
          <div className="text-xl font-mono font-bold text-slate-900 mt-1">
            {campaignReport.totals?.cap_violations || 0}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">cap fired · we kept upside</div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] text-slate-500">
          {new Set((dailyReport?.data || []).map((r) => r.date)).size} day(s) · {dailyReport?.start_date} → {dailyReport?.end_date} · click a day to see per-publisher breakdown
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => downloadDailyCsv(dailyReport)}
          disabled={!dailyReport?.data?.length}
          data-testid="sn-reconciliation-csv"
        >
          <DownloadSimple size={14} className="mr-1.5" />
          Download CSV
        </Button>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden bg-white" data-testid="sn-reconciliation-table">
        {days.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No data yet for this window.</div>
        ) : (
          days.map((d) => {
            const isOpen = openReconcDate === d.date;
            return (
              <div key={d.date} className="border-b border-slate-100 last:border-0">
                <button
                  type="button"
                  onClick={() => onToggleDate(isOpen ? null : d.date)}
                  className={`w-full grid grid-cols-[12px_120px_1fr_auto_auto_auto_auto] gap-4 items-center px-4 py-3 text-left hover:bg-slate-50 transition-colors ${isOpen ? "bg-slate-50" : ""}`}
                  data-testid={`sn-reconc-day-${d.date}`}
                >
                  <CaretDown size={12} weight="bold" className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  <div className="font-mono text-sm font-bold text-slate-900">{d.date}</div>
                  <div className="text-xs text-slate-500">
                    {d.rows.length} pub{d.rows.length === 1 ? "" : "s"}
                    <span className="mx-2">·</span>
                    <span className="font-mono">{d.our_clicks}</span> our clicks
                    <span className="mx-2">·</span>
                    <span className="font-mono">{d.feed_clicks}</span> feed clicks
                  </div>
                  <div className="text-xs text-right min-w-[90px]">
                    <span className="text-slate-500">rev </span>
                    <span className="font-mono font-bold text-slate-900">${d.feed_revenue.toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-right min-w-[90px]">
                    <span className="text-slate-500">pub </span>
                    <span className="font-mono font-bold text-emerald-700">${d.pub_earnings.toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-right min-w-[90px]">
                    <span className="text-slate-500">margin </span>
                    <span className="font-mono font-bold text-[#002FA7]">${d.margin.toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-right min-w-[70px]">
                    {d.any_cap && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold mr-1">CAP</span>}
                    {d.any_pending && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[9px] font-bold">PEND</span>}
                  </div>
                </button>
                {isOpen && (
                  <div className="bg-slate-50/60 border-t border-slate-100 px-4 py-3" data-testid={`sn-reconc-day-${d.date}-expanded`}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Campaign</th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Publisher</th>
                          <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Feed EPC</th>
                          <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Our clicks</th>
                          <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Feed clicks</th>
                          <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Rev (got)</th>
                          <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Pub paid</th>
                          <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#002FA7] font-bold">Margin</th>
                          <th className="text-center px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.rows.map((r, i) => (
                          <tr key={`${r.campaign_label}-${r.publisher_sub_id || i}`} className="border-b border-slate-100 last:border-0 hover:bg-white">
                            <td className="px-3 py-2 text-sm text-slate-700">{r.campaign_label || "—"}</td>
                            <td className="px-3 py-2">
                              <div className="text-sm text-slate-900 truncate max-w-[200px]" title={r.publisher_email}>
                                {r.publisher_company || r.publisher_email || <span className="text-slate-400">(unmatched)</span>}
                              </div>
                              <div className="text-[10px] font-mono text-slate-400">{r.publisher_sub_id || ""}</div>
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-sm">${Number(r.feed_epc).toFixed(4)}</td>
                            <td className="px-3 py-2 text-right font-mono text-sm">{r.our_clicks}</td>
                            <td className="px-3 py-2 text-right font-mono text-sm">{r.feed_clicks}</td>
                            <td className="px-3 py-2 text-right font-mono text-sm font-bold">${Number(r.feed_revenue).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right font-mono text-sm font-bold text-emerald-700">${Number(r.pub_earnings).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right font-mono text-sm font-bold text-[#002FA7]">${Number(r.margin).toFixed(2)}</td>
                            <td className="px-3 py-2 text-center">
                              {r.cap_fired ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold">CAP</span>
                              ) : r.pending ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold">PENDING</span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="mt-3 text-[11px] text-slate-500">
        <span className="font-semibold">CAP</span> = day where feed EPC ≥ <span className="font-mono">${Number(dailyReport?.payout_cap_usd ?? campaignReport.payout_cap_usd ?? 0.27).toFixed(2)}</span> — we paid pub the ceiling, kept the rest as margin.
        <span className="mx-2">·</span>
        <span className="font-semibold">PENDING</span> = our tracker logged clicks but Shopnomix hasn&apos;t reported revenue yet (typically T+1h to T+24h).
      </div>
    </>
  );
}
